import { graphql } from 'gatsby';
import { applyPaddingCSS } from '@/utils/generatePadding';
import { RichTextElement } from '@kontent-ai/gatsby-components';
import {
  StyledContainer,
  StyledSection,
  StyledBodyText,
  StyledItems,
  StyledTitle,
  StyledDescriptionContainer,
  StyledButtonContainer,
} from './StatBarBlock.styles';
import StatBarBlockItem from './components/StatBarBlockItem';
import Link from '@/components/Link/Link';
import { isRichTextValid } from '@/components/RichTextBlock/utils';
import useResolveRichTextInternalLink from '@/components/RichTextBlock/hooks/useResolveRichTextInternalLink';
import type { BlockHTMLId, BlockHTMLClassName } from '@/utils/renderBlock';

const StatBarBlock = ({
  statBarBlockElements: elements,
  system,
  id,
  className,
}: Queries.STATBAR_BLOCKFragment & BlockHTMLId & BlockHTMLClassName) => {
  const { title, body, items, button } = elements;
  const resolveRichTextInternalLink = useResolveRichTextInternalLink();
  const hasBody = isRichTextValid(body?.value);

  return (
    <StyledSection
      className={className}
      css={applyPaddingCSS(elements)}
      data-codename={system.codename}
      id={id}
    >
      <StyledContainer>
        <StyledDescriptionContainer>
          {!!title?.value && <StyledTitle>{title.value}</StyledTitle>}
          {hasBody && (
            <StyledBodyText>
              <RichTextElement
                value={body.value}
                links={body.links}
                resolveLink={resolveRichTextInternalLink}
              />
            </StyledBodyText>
          )}
        </StyledDescriptionContainer>
        <StyledItems hasButton={!!button?.value}>
          {items.value.map((item) => (
            <StatBarBlockItem key={item.system.id} {...item} />
          ))}
        </StyledItems>
        {button?.value && (
          <StyledButtonContainer>
            <Link {...button.value} variant="tertiary" />
          </StyledButtonContainer>
        )}
      </StyledContainer>
    </StyledSection>
  );
};

export default StatBarBlock;

export const fragment = graphql`
  fragment STATBAR_BLOCK on kontent_item_statbar_block {
    __typename
    statBarBlockElements: elements {
      ...COMPONENT_A_B_TEST
      ...PADDING
      body {
        value
        links {
          link_id
          codename
        }
      }
      title {
        value
      }
      button {
        value {
          ...LINK
        }
      }
      items {
        value {
          ...STATBAR_BLOCK_ITEM
        }
      }
    }
    system {
      codename
      type
      id
    }
  }
`;
